/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */
'use client';

import { useContext, useEffect, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { DateRangeContext } from '@/context';
import { SelectedDateRange } from '@/providers/DateRangeProvider';
import { useLanguage } from '@/providers/LanguageProvider';
import { AppGraph } from 'types/api-data';

import { useAppDetails } from '@/hooks/useAppDetails';
import { useMeasuresFromLocalStorage } from '@/hooks/useMeasuresFromLocalStorage';

import { TitleDropDown } from '../detail-page';
import ArchitectureServiceDialog from '../display-page/architecture/architecture-service-dialog';
import { Icons } from '../icons';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import EvalStatusPopover from './eval-status-popover';
import TracesMeasurePill from './traces-measure-pill';

export type ToFromDateRange = {
  from: Date | null | undefined;
  to: Date | null | undefined;
};

export const TitleBar = ({
  data,
  activeTab,
}: {
  data: AppGraph;
  activeTab: string;
}) => {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const mostRecentParam = searchParams.get('mostRecent');

  const isMostRecentMode =
    mostRecentParam !== null && Number(mostRecentParam) > 0;

  const { messages } = useLanguage();

  const { data: applicationDetails } = useAppDetails();

  const dateRangeContext = useContext(DateRangeContext);
  const selectedDateRange: SelectedDateRange = dateRangeContext?.range;

  const [toFromDateRange, setToFromDateRange] = useState<ToFromDateRange>({
    from: null,
    to: null,
  });

  const [selectedPreset, setSelectedPreset] = useState<
    string | null | undefined
  >();

  useEffect(() => {
    if (selectedDateRange?.from && selectedDateRange?.to) {
      const toFromDate = {
        from: selectedDateRange?.from,
        to: selectedDateRange?.to,
      };
      setToFromDateRange(toFromDate);
    }
  }, [selectedDateRange?.from, selectedDateRange?.to]);

  useEffect(() => {
    if (selectedDateRange?.selectedPreset) {
      // If context provides a preset, use it
      setSelectedPreset(selectedDateRange.selectedPreset);
    } else if (selectedDateRange?.from && selectedDateRange?.to) {
      // If there's no preset but manual dates selected
      setSelectedPreset(null);
    } else if (applicationDetails?.default_time_range) {
      // If no date range is selected, use the default from applicationDetails
      setSelectedPreset(applicationDetails.default_time_range);
    }
  }, [
    selectedDateRange?.selectedPreset,
    selectedDateRange?.from,
    selectedDateRange?.to,
    applicationDetails.default_time_range,
  ]);

  useEffect(() => {
    setSelectedPreset(applicationDetails.default_time_range);
  }, [activeTab]);
  // Fetch measures data from local storage
  const { cardMeasures } = useMeasuresFromLocalStorage();

  const filteredAppMeasures = Object.entries(cardMeasures).filter(
    ([appName]) => {
      return appName === data.name;
    }
  );

  // Destructure measures count data and set { errors: 0, not_meet_sla: 0, test_failures: 0 } if the measures data is not found in app.
  const [, measures] =
    filteredAppMeasures.length > 0
      ? filteredAppMeasures[0]
      : [
          null,
          {
            errors: 0,
            not_meet_sla: 0,
            not_meet_budget_sla: 0,
            test_failures: 0,
          },
        ];

  const isArchitecturePage = pathname.includes('/architecture');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUrl, setSelectedUrl] = useState<string | null>(null);

  const handleEndpointUrlClick = (url: string) => {
    setOpenDialog(true);
    setSelectedUrl(url);
  };
  return (
    <div className="mb-3">
      <div className="flex items-center justify-between">
        <div className="flex h-full items-center">
          <h1 className="text-h1 text-body-primary">{data.display_name}</h1>
          {data.application_url && (
            <div className="ml-4">
              <button
                onClick={() => {
                  const endpointUrl = data.application_url;
                  if (endpointUrl) {
                    handleEndpointUrlClick(endpointUrl);
                  }
                }}
                className="flex h-7 items-center gap-1 pl-2 pr-3"
              >
                {Icons.PopOut()}
                <p className="text-btn-label text-ocean hover:underline hover:underline-offset-2">
                  {messages?.General.launch}
                </p>
              </button>
              {selectedUrl && (
                <ArchitectureServiceDialog
                  endpointUrl={selectedUrl}
                  openDialog={openDialog}
                  setOpenDialog={setOpenDialog}
                />
              )}
            </div>
          )}

          <div className="ml-4 flex items-center [&>*:not(:first-child)]:relative [&>*:not(:first-child)]:before:mx-4 [&>*:not(:first-child)]:before:block [&>*:not(:first-child)]:before:h-7 [&>*:not(:first-child)]:before:w-px [&>*:not(:first-child)]:before:bg-line-tertiary [&>*:not(:first-child)]:before:content-['']">
            {!isMostRecentMode && (
              <TracesMeasurePill
                measures={measures}
                data={data}
                selectedPreset={selectedPreset}
                toFromDateRange={toFromDateRange}
                lastDiscoveredAt={data?.last_discovery_at}
              />
            )}
            {!isArchitecturePage && <EvalStatusPopover appName={data.name} />}
          </div>
        </div>

        <div className="flex items-center gap-x-5">
          {/* The below two icons are removed till such time the feature becomes public */}
          {/* {Icons.Share('stroke-form-input-disabled')}
          {Icons.Flag('stroke-form-input-disabled')} */}
          <div className="cursor-pointer">
            <TitleDropDown title={data.display_name} name={data.name} />
          </div>
        </div>
      </div>
      <div className="flex min-w-0 gap-4 text-sm text-form-input-disabled">
        <span className="whitespace-nowrap">
          {messages?.ApplicationDetailPage?.id}: {data.name}
        </span>
        {data?.owner.trim() ? (
          <span className="whitespace-nowrap">
            {messages?.General?.owner}:{' '}
            <span className="text-link-active">{data?.owner}</span>
          </span>
        ) : (
          ''
        )}
        {data?.description && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="mr-20  cursor-pointer overflow-hidden truncate whitespace-nowrap">
                  {messages?.ApplicationForm.description}: {data?.description}
                </span>
              </TooltipTrigger>

              <TooltipContent
                side="bottom"
                align="start"
                sideOffset={4}
                className="mr-20 max-w-[calc(100vw-850px)] break-words"
              >
                {data.description}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    </div>
  );
};
